import type { GetServerSidePropsContext } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import {
  buildSpacesCanonicalDestination,
  normalizeLegacyEncryptedId,
  resolveSpaceUuidByEncryptedId,
} from 'features/space/lib/resolveEcboSpaceUuid'

type Props = {
  redirect?: string
}

/**
 * Legacy `/space/{encryptedId}` → canonical `/spaces/{uuid}` へ 301。
 */
export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, query, resolvedUrl } = context
  const segments = Array.isArray(query.slug) ? query.slug : []
  const isWalkin = segments.length >= 2 && segments.at(-1) === 'walkin'

  const encryptedId = normalizeLegacyEncryptedId(query.slug)
  if (!encryptedId) {
    return { notFound: true }
  }

  const spaceUuid = await resolveSpaceUuidByEncryptedId(encryptedId, 'normal')
  if (!spaceUuid) {
    return { notFound: true }
  }

  if (isWalkin) {
    // Redirect on the client so GA can record the `/walkin` page view before
    // navigating to the canonical space detail URL.
    return {
      props: {
        redirect: buildSpacesCanonicalDestination(locale, spaceUuid, resolvedUrl),
        ...(await serverSideTranslations(locale, ['common'])),
      },
    }
  }

  return {
    redirect: {
      destination: buildSpacesCanonicalDestination(locale, spaceUuid, resolvedUrl),
      statusCode: 301,
    },
  }
}

export default function SpaceDetailRedirectPage({ redirect }: Props) {
  const router = useRouter()

  useEffect(() => {
    if (redirect) router.replace(redirect)
  }, [redirect, router])

  return null
}
